import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";

/**
 * ── Cuántas filas por página (Anahí, 29-ago) ─────────────────────────
 * "Que se muestren 5, 10 y 20 items, por default siempre 5, pero se
 * puede pedir mostrar más."
 *
 * Cinco por defecto en TODO el sistema: una tabla corta se abarca de un
 * vistazo y quien necesita ver más lo pide. Diez llenaba la pantalla en
 * secciones donde casi siempre hay tres filas.
 */
export const PER_PAGE_OPTIONS = [5, 10, 20] as const;
export const DEFAULT_PER_PAGE = 5;

export interface PaginationProps {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  /**
   * Cambiar el tamaño. Opcional: una tabla sin él sigue funcionando
   * igual, solo que sin selector — así ninguna pantalla se rompe
   * mientras se van conectando todas.
   */
  onPerPageChange?: (perPage: number) => void;
  className?: string;
}

/**
 * Paginación de una lista servida por el API.
 *
 * Enseña el RANGO ("1–24 de 87") y no solo el número de página: con el
 * rango, quien busca sabe cuánto le falta; con "página 1 de 4" tiene que
 * multiplicar. Candidata a `shared/ui/` — no sabe qué se está paginando.
 *
 * Los botones se deshabilitan en los extremos en vez de desaparecer: un
 * control que se esfuma mueve el resto de la barra bajo el cursor.
 */
export function Pagination({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  className,
}: PaginationProps) {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-ds-border px-4 py-3",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[12px] text-ds-ink-3" aria-live="polite">
          {t("Mostrando")} <strong className="text-ds-ink">{from}</strong>–
          <strong className="text-ds-ink">{to}</strong> {t("de")}{" "}
          <strong className="text-ds-ink">{total}</strong>
        </p>

        {onPerPageChange && (
          <label className="flex items-center gap-1.5 text-[12px] text-ds-ink-3">
            {t("Ver")}
            <select
              aria-label={t("Filas por página")}
              value={perPage}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
              className="rounded-ds-pill border border-ds-border bg-white px-2 py-1 text-[12px] font-semibold text-ds-ink outline-none transition focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          leftIcon={<ChevronLeft />}
        >
          {t("Anterior")}
        </Button>
        <span className="text-[12px] font-semibold text-ds-ink-3">
          {t("Página")} {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          rightIcon={<ChevronRight />}
        >
          {t("Siguiente")}
        </Button>
      </div>
    </div>
  );
}
